import React from "react";
import { renderRichText } from "./richText";

// Lightweight Markdown for sticky notes.
//   Blocks:  # .. #### headings · "- " / "* " bullets · "1. " numbered ·
//            "[ ]" / "[x]" (optionally after "- ") live checkboxes
//   Inline:  **bold** · *italic* / _italic_ · __underline__ · ~~strike~~ · `code`
//   Plain text still gets clickable links + bold-red dd-mm-yyyy dates via
//   renderRichText. Everything is plain text under the hood, so notes stay
//   portable (export, sync) and never need an HTML sanitizer.

const INLINE_RE =
  /(\*\*([^*\n]+?)\*\*)|(__([^_\n]+?)__)|(~~([^~\n]+?)~~)|(`([^`\n]+?)`)|(\*([^*\n]+?)\*)|((?:^|(?<=[\s(]))_([^_\n]+?)_(?=$|[\s).,;:!?]))/g;

function renderInline(text, keyPrefix, depth = 0) {
  if (!text) return null;
  if (depth > 4) return renderRichText(text, keyPrefix);
  const out = [];
  let last = 0;
  let i = 0;
  const re = new RegExp(INLINE_RE);
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last)
      out.push(
        <React.Fragment key={`${keyPrefix}-t${i}`}>
          {renderRichText(text.slice(last, m.index), `${keyPrefix}-t${i}`)}
        </React.Fragment>,
      );
    const k = `${keyPrefix}-f${i}`;
    if (m[1]) out.push(<strong key={k} className="font-bold">{renderInline(m[2], k, depth + 1)}</strong>);
    else if (m[3]) out.push(<u key={k}>{renderInline(m[4], k, depth + 1)}</u>);
    else if (m[5]) out.push(<s key={k} className="opacity-70">{renderInline(m[6], k, depth + 1)}</s>);
    else if (m[7])
      out.push(
        <code key={k} className="px-1 rounded bg-black/10 font-mono text-[0.9em]">
          {m[8]}
        </code>,
      );
    else if (m[9]) out.push(<em key={k}>{renderInline(m[10], k, depth + 1)}</em>);
    else if (m[11]) out.push(<em key={k}>{renderInline(m[12], k, depth + 1)}</em>);
    last = m.index + m[0].length;
    i += 1;
    if (m[0].length === 0) re.lastIndex++;
  }
  if (last < text.length)
    out.push(
      <React.Fragment key={`${keyPrefix}-t${i}`}>
        {renderRichText(text.slice(last), `${keyPrefix}-t${i}`)}
      </React.Fragment>,
    );
  return out;
}

const HEADING_CLS = {
  1: "text-xl font-bold leading-tight mt-1 mb-0.5",
  2: "text-lg font-bold leading-tight mt-1 mb-0.5",
  3: "text-base font-semibold leading-tight mt-0.5",
  4: "text-sm font-semibold uppercase tracking-wide opacity-80 mt-0.5",
};

const CHECK_RE = /^(\s*)(?:[-*•]\s+)?\[([ xX]?)\]\s?(.*)$/;
const BULLET_RE = /^\s*[-*•]\s+(.*)$/;
const NUM_RE = /^\s*(\d+)[.)]\s+(.*)$/;
const HEAD_RE = /^(#{1,4})\s+(.*)$/;

/**
 * Render note text as React nodes. `onToggleCheckbox(lineIndex)` makes the
 * checkboxes interactive (omit for read-only).
 */
export function renderNoteMarkdown(text, keyPrefix = "nm", { onToggleCheckbox } = {}) {
  const lines = String(text || "").replace(/\r/g, "").split("\n");
  const blocks = [];
  let list = null; // { type: "ul"|"ol", items: [] }
  const flush = () => {
    if (!list) return;
    const Tag = list.type;
    blocks.push(
      <Tag
        key={`${keyPrefix}-l${blocks.length}`}
        className={`${Tag === "ul" ? "list-disc" : "list-decimal"} pl-5 my-0.5 space-y-0.5`}
      >
        {list.items}
      </Tag>,
    );
    list = null;
  };

  lines.forEach((line, idx) => {
    const k = `${keyPrefix}-${idx}`;
    let m;
    if ((m = line.match(HEAD_RE))) {
      flush();
      const lvl = m[1].length;
      const Tag = `h${lvl}`;
      blocks.push(
        <Tag key={k} className={HEADING_CLS[lvl]} style={{ fontFamily: "inherit" }}>
          {renderInline(m[2], k)}
        </Tag>,
      );
    } else if ((m = line.match(CHECK_RE))) {
      flush();
      const checked = m[2].toLowerCase() === "x";
      blocks.push(
        <div key={k} className="flex items-start gap-1.5">
          <input
            type="checkbox"
            checked={checked}
            readOnly={!onToggleCheckbox}
            onChange={() => onToggleCheckbox && onToggleCheckbox(idx)}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
            className="mt-[3px] w-3.5 h-3.5 flex-shrink-0 accent-current cursor-pointer"
            aria-label="Toggle item"
          />
          <span className={checked ? "line-through opacity-60" : ""}>{renderInline(m[3], k)}</span>
        </div>,
      );
    } else if ((m = line.match(BULLET_RE))) {
      if (!list || list.type !== "ul") {
        flush();
        list = { type: "ul", items: [] };
      }
      list.items.push(<li key={k}>{renderInline(m[1], k)}</li>);
    } else if ((m = line.match(NUM_RE))) {
      if (!list || list.type !== "ol") {
        flush();
        list = { type: "ol", items: [] };
      }
      list.items.push(
        <li key={k} value={Number(m[1]) || undefined}>
          {renderInline(m[2], k)}
        </li>,
      );
    } else {
      flush();
      blocks.push(<div key={k}>{line.trim() ? renderInline(line, k) : " "}</div>);
    }
  });
  flush();
  return blocks;
}

const isCheckedLine = (line) => {
  const m = line.match(CHECK_RE);
  return !!m && m[2].toLowerCase() === "x";
};

/**
 * Flip the checkbox on one line of note text and reorder it within its
 * checklist group (consecutive checkbox lines): a ticked item moves to the
 * bottom of the group, an unticked one to just after the last unticked item.
 */
export function toggleCheckboxLine(text, lineIndex) {
  const lines = String(text || "").replace(/\r/g, "").split("\n");
  const line = lines[lineIndex];
  if (line === undefined) return text;
  const m = line.match(CHECK_RE);
  if (!m) return text;
  const checked = m[2].toLowerCase() === "x";
  const flipped = line.replace(/\[([ xX]?)\]/, checked ? "[ ]" : "[x]");

  let start = lineIndex;
  while (start > 0 && CHECK_RE.test(lines[start - 1])) start -= 1;
  let end = lineIndex;
  while (end < lines.length - 1 && CHECK_RE.test(lines[end + 1])) end += 1;

  const group = lines.slice(start, end + 1);
  group.splice(lineIndex - start, 1);
  if (checked) {
    let lastUnchecked = -1;
    group.forEach((l, i) => {
      if (!isCheckedLine(l)) lastUnchecked = i;
    });
    group.splice(lastUnchecked + 1, 0, flipped);
  } else {
    group.push(flipped);
  }
  lines.splice(start, end - start + 1, ...group);
  return lines.join("\n");
}

// ---------- dated checklist items -> Reminders ----------

// A date written as DD.MM.YYYY (single-digit day/month allowed: 5.1.2027).
const DOT_DATE_RE = /(^|[^\d.])(\d{1,2})\.(\d{1,2})\.(\d{4})(?![\d.])/;

/**
 * Unticked checkbox lines in a note that carry a DD.MM.YYYY date, e.g.
 * "[ ] Submit camera-ready 12.10.2026". Each becomes its own reminder; once the
 * box is ticked the line no longer qualifies, so the reminder disappears.
 * Returns [{ lineIndex, date: "YYYY-MM-DD", text }]. Only checkbox lines count;
 * headings, bullets, numbered lines and plain text are ignored. If a line has
 * several dates, the first one is used.
 */
export function datedNoteTasks(text) {
  const out = [];
  String(text || "")
    .replace(/\r/g, "")
    .split("\n")
    .forEach((line, lineIndex) => {
      const m = line.match(CHECK_RE);
      if (!m || m[2].toLowerCase() === "x") return;
      const d = m[3].match(DOT_DATE_RE);
      if (!d) return;
      const day = Number(d[2]);
      const month = Number(d[3]);
      const year = Number(d[4]);
      const probe = new Date(year, month - 1, day);
      if (
        probe.getFullYear() !== year ||
        probe.getMonth() !== month - 1 ||
        probe.getDate() !== day
      )
        return; // not a real calendar date (e.g. 31.02.2027)
      const date = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      const label =
        m[3]
          .replace(DOT_DATE_RE, "$1")
          .replace(/\s*[-–—:|,@]\s*$/, "")
          .replace(/^\s*[-–—:|,@]\s*/, "")
          .replace(/\*\*|__|~~|`/g, "")
          .replace(/\s{2,}/g, " ")
          .trim() || "Note task";
      out.push({ lineIndex, date, text: label });
    });
  return out;
}

// ---------- editor helpers (operate on a textarea's value + selection) ----------

/** Wrap the selection with `left`/`right` markers (or toggle them off). */
export function wrapSelection(text, start, end, left, right = left) {
  const before = text.slice(0, start);
  const sel = text.slice(start, end);
  const after = text.slice(end);
  // Toggle off if already wrapped.
  if (sel.startsWith(left) && sel.endsWith(right) && sel.length >= left.length + right.length) {
    const inner = sel.slice(left.length, sel.length - right.length);
    return { text: before + inner + after, start, end: start + inner.length };
  }
  if (before.endsWith(left) && after.startsWith(right)) {
    return {
      text: before.slice(0, -left.length) + sel + after.slice(right.length),
      start: start - left.length,
      end: end - left.length,
    };
  }
  const t = before + left + sel + right + after;
  return { text: t, start: start + left.length, end: end + left.length };
}

const lineBounds = (text, start, end) => {
  const ls = text.lastIndexOf("\n", start - 1) + 1;
  let le = text.indexOf("\n", end);
  if (le === -1) le = text.length;
  return { ls, le };
};

/**
 * Apply/toggle a line prefix over every line touched by the selection.
 * `kind`: "bullet" | "number" | "check" | "heading" (heading cycles # → ## → ### → none).
 */
export function applyLinePrefix(text, start, end, kind) {
  const { ls, le } = lineBounds(text, start, end);
  const lines = text.slice(ls, le).split("\n");
  const strip = (l) =>
    l.replace(/^(\s*)(?:#{1,4}\s+|[-*•]\s+\[[ xX]?\]\s?|\[[ xX]?\]\s?|[-*•]\s+|\d+[.)]\s+)/, "$1");
  const has = (l, re) => re.test(l);
  let out;
  if (kind === "heading") {
    out = lines.map((l) => {
      const m = l.match(/^(\s*)(#{1,4})\s+(.*)$/);
      if (!m) return `${l.match(/^\s*/)[0]}# ${strip(l).trim()}`;
      const n = m[2].length;
      return n >= 3 ? `${m[1]}${m[3]}` : `${m[1]}${"#".repeat(n + 1)} ${m[3]}`;
    });
  } else if (kind === "bullet") {
    const all = lines.every((l) => has(l, /^\s*[-*•]\s+(?!\[)/));
    out = lines.map((l) => (all ? strip(l) : `${l.match(/^\s*/)[0]}- ${strip(l).trim()}`));
  } else if (kind === "number") {
    const all = lines.every((l) => has(l, /^\s*\d+[.)]\s+/));
    out = lines.map((l, i) => (all ? strip(l) : `${l.match(/^\s*/)[0]}${i + 1}. ${strip(l).trim()}`));
  } else if (kind === "check") {
    const all = lines.every((l) => has(l, /^\s*(?:[-*•]\s+)?\[[ xX]?\]/));
    out = lines.map((l) => (all ? strip(l) : `${l.match(/^\s*/)[0]}- [ ] ${strip(l).trim()}`));
  } else {
    return { text, start, end };
  }
  const block = out.join("\n");
  return { text: text.slice(0, ls) + block + text.slice(le), start: ls, end: ls + block.length };
}
